/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Suspense } from 'react';
import {
	ClientNewsSource,
	PagedModelClientPost,
	GetAllExternalPostsByNewsSourcePostTypesEnum as PostTypeEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import SearchView from './Search.view';
import FiltersView from './Filters.view';
import BarLoadingIndicator from '../../shared/views/LoadingIndicators/BarLoadingIndicator.view';
import { Box, Grid, Pagination, Typography } from '@mui/material';
const ExternalPostView = React.lazy(() => import('./ExternalPost.view'));

interface ExternalPostType {
	name: string;
	postType?: PostTypeEnum;
}

interface PaginationInterface {
	page: number;
	rowsPerPage: number;
	totalElements: number;
	totalPages: number;
}
interface Props {
	externalPosts: PagedModelClientPost | undefined;
	pageOfClientNewsSource: ClientNewsSource[];
	externalPostsTypes: ExternalPostType[];
	loading: boolean;
	pagination: PaginationInterface;
	selectedPostTypes: string | undefined;
	handleChangePostType: (newPostType: string) => void;
	handleSearch: (searchString: string) => void;
	handleDelete: (PostID: string | undefined) => void;
	handleChangePage: (event: React.ChangeEvent<unknown>, newPage: number) => void;
}

const ExternalPostsView = (props: Props): JSX.Element => {
	const {
		externalPosts,
		externalPostsTypes,
		loading,
		pagination,
		handleChangePage,
		handleChangePostType,
		handleSearch,
		handleDelete,
		selectedPostTypes,
		pageOfClientNewsSource
	} = props;

	return (
		<div>
			<div style={{ marginBottom: '15px' }}>
				{loading ? (
					<BarLoadingIndicator loading={loading} />
				) : (
					<Suspense fallback={<BarLoadingIndicator loading={loading} />}>
						<Grid container spacing={2}>
							<Grid item xs={12} md={5}>
								<SearchView pageOfClientNewsSource={pageOfClientNewsSource} handleSearch={handleSearch} />
							</Grid>
							<Grid item xs={12} md={5}>
								<FiltersView
									externalPostsTypes={externalPostsTypes}
									selectedPostTypes={selectedPostTypes}
									handleChangePostType={handleChangePostType}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={1}>
							{externalPosts &&
								externalPosts.content?.length !== 0 &&
								externalPosts.content?.map(externalPost => {
									return (
										<Grid item xs={12} md={6} lg={4} key={externalPost.id}>
											<ExternalPostView externalPost={externalPost} handleDelete={handleDelete} />
										</Grid>
									);
								})}
						</Grid>
					</Suspense>
				)}
			</div>
			<Box display="flex" alignItems="center" justifyContent="center">
				{externalPosts?.content?.length === 0 ? (
					<Box marginTop={3} marginRight={'auto'}>
						<Typography variant="body1"> Keine Daten zum Anzeigen </Typography>
					</Box>
				) : (
					<Pagination
						count={pagination.totalPages}
						page={pagination.page + 1}
						siblingCount={1}
						boundaryCount={2}
						defaultPage={1}
						color="primary"
						onChange={handleChangePage}
						size="large"
					/>
				)}
			</Box>
		</div>
	);
};

export default ExternalPostsView;
